import { supabase } from "../lib/supabase";
import type {
  AdminVisitUpdateInput,
  VisitFilters,
  VisitPage,
  VisitRecord,
} from "../types/domain";
import { assertCondition } from "../utils/errors";
import { formatDateForFilter } from "../utils/dateTime";
import { getCurrentSession } from "./auth";

export const ADMIN_PAGE_SIZE = 50;
const OPEN_VISITS_LIMIT = 500;
const EXPORT_BATCH_SIZE = 1000;

// `!inner` permette di filtrare lato server sui campi del visitatore.
const VISIT_SELECT = `
  id,
  visitor_id,
  visit_date,
  checkin_at,
  checkout_at,
  checkin_signature_path,
  checkout_signature_path,
  checkout_closed_by,
  status,
  notes,
  created_at,
  updated_at,
  visitors!inner (
    id,
    first_name,
    last_name,
    company
  )
`;

type VisitorRow = {
  id: string;
  first_name: string;
  last_name: string;
  company: string;
};

type VisitRow = {
  id: string;
  visitor_id: string;
  visit_date: string;
  checkin_at: string;
  checkout_at: string | null;
  checkin_signature_path: string;
  checkout_signature_path: string | null;
  checkout_closed_by: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  visitors: VisitorRow | VisitorRow[] | null;
};

type VisitQueryOptions = {
  withCount?: boolean;
  signal?: AbortSignal;
};

function mapVisitRow(row: VisitRow): VisitRecord {
  const visitor = Array.isArray(row.visitors) ? row.visitors[0] : row.visitors;
  assertCondition(visitor, `Visitatore associato alla visita ${row.id} non disponibile.`);

  return {
    visitId: row.id,
    visitorId: row.visitor_id,
    firstName: visitor.first_name,
    lastName: visitor.last_name,
    company: visitor.company,
    visitDate: row.visit_date,
    checkinAt: row.checkin_at,
    checkoutAt: row.checkout_at,
    checkinSignaturePath: row.checkin_signature_path,
    checkoutSignaturePath: row.checkout_signature_path,
    checkoutClosedBy: row.checkout_closed_by,
    status: row.status,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function escapeLikePattern(value: string) {
  return value.replace(/[\\%_]/g, "\\$&");
}

function buildVisitsQuery(filters: VisitFilters, options: VisitQueryOptions = {}) {
  let query = supabase
    .from("visits")
    .select(VISIT_SELECT, options.withCount ? { count: "exact" } : undefined);

  if (filters.date) {
    query = query.eq("visit_date", filters.date);
  }

  const textFilters = [
    ["visitors.first_name", filters.firstName],
    ["visitors.last_name", filters.lastName],
    ["visitors.company", filters.company],
  ] as const;

  for (const [column, value] of textFilters) {
    const term = value.trim();
    if (term) {
      query = query.ilike(column, `%${escapeLikePattern(term)}%`);
    }
  }

  if (options.signal) {
    query = query.abortSignal(options.signal);
  }

  return query;
}

export async function fetchAdminVisitsPage(
  filters: VisitFilters,
  offset = 0,
  limit = ADMIN_PAGE_SIZE,
  signal?: AbortSignal,
): Promise<VisitPage> {
  const { data, error, count } = await buildVisitsQuery(filters, {
    withCount: offset === 0,
    signal,
  })
    .order("checkin_at", { ascending: false })
    .order("id", { ascending: true })
    .range(offset, offset + limit - 1);

  if (error) {
    throw new Error(error.message);
  }

  return {
    records: ((data ?? []) as unknown as VisitRow[]).map(mapVisitRow),
    total: count ?? null,
  };
}

export async function fetchAdminOpenVisits(
  filters: VisitFilters,
  signal?: AbortSignal,
): Promise<VisitRecord[]> {
  const { data, error } = await buildVisitsQuery(filters, { signal })
    .eq("status", "open")
    .order("checkin_at", { ascending: false })
    .limit(OPEN_VISITS_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  return ((data ?? []) as unknown as VisitRow[]).map(mapVisitRow);
}

export async function fetchAllAdminVisits(filters: VisitFilters): Promise<VisitRecord[]> {
  const records: VisitRecord[] = [];

  for (let offset = 0; ; offset += EXPORT_BATCH_SIZE) {
    const page = await fetchAdminVisitsPage(filters, offset, EXPORT_BATCH_SIZE);
    records.push(...page.records);

    if (page.records.length < EXPORT_BATCH_SIZE) {
      return records;
    }
  }
}

export async function fetchAdminVisitsByIds(visitIds: string[]): Promise<VisitRecord[]> {
  if (visitIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("visits")
    .select(VISIT_SELECT)
    .in("id", visitIds);

  if (error) {
    throw new Error(error.message);
  }

  return ((data ?? []) as unknown as VisitRow[]).map(mapVisitRow);
}

export async function fetchAdminVisitById(visitId: string) {
  const records = await fetchAdminVisitsByIds([visitId]);
  return records[0] ?? null;
}

export async function closeAdminVisits(visitIds: string[], checkoutAtIso: string) {
  const { data, error } = await supabase.rpc("admin_close_visits", {
    p_visit_ids: visitIds,
    p_checkout_at: checkoutAtIso,
  });

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []) as string[];
}

async function resolveCheckoutClosedBy(
  currentRecord: VisitRecord,
  checkoutAtIso: string | null,
) {
  if (!checkoutAtIso) {
    return null;
  }

  if (currentRecord.checkoutSignaturePath || currentRecord.checkoutClosedBy) {
    return currentRecord.checkoutClosedBy;
  }

  // Uscita inserita dall'admin senza firma del visitatore: tracciamo chi l'ha registrata.
  const session = await getCurrentSession();
  assertCondition(session?.user.id, "Sessione amministratore non disponibile.");

  return session.user.id;
}

export async function updateAdminVisitRecord(
  currentRecord: VisitRecord,
  input: AdminVisitUpdateInput,
) {
  const visitorPatch: Record<string, string> = {};
  if (currentRecord.firstName !== input.firstName) {
    visitorPatch.first_name = input.firstName;
  }
  if (currentRecord.lastName !== input.lastName) {
    visitorPatch.last_name = input.lastName;
  }
  if (currentRecord.company !== input.company) {
    visitorPatch.company = input.company;
  }

  const visitPatch = {
    visit_date: formatDateForFilter(input.checkinAtIso),
    checkin_at: input.checkinAtIso,
    checkout_at: input.checkoutAtIso,
    checkout_closed_by: await resolveCheckoutClosedBy(currentRecord, input.checkoutAtIso),
    status: input.checkoutAtIso ? "closed" : "open",
    notes: input.notes,
  };

  const previousVisitorState = {
    first_name: currentRecord.firstName,
    last_name: currentRecord.lastName,
    company: currentRecord.company,
  };

  if (Object.keys(visitorPatch).length > 0) {
    const { error: visitorError } = await supabase
      .from("visitors")
      .update(visitorPatch)
      .eq("id", input.visitorId);

    if (visitorError) {
      throw new Error(visitorError.message);
    }
  }

  const { error: visitError } = await supabase
    .from("visits")
    .update(visitPatch)
    .eq("id", input.visitId);

  if (visitError) {
    if (Object.keys(visitorPatch).length > 0) {
      await supabase
        .from("visitors")
        .update(previousVisitorState)
        .eq("id", input.visitorId);
    }

    throw new Error(visitError.message);
  }

  const refreshedRecord = await fetchAdminVisitById(input.visitId);
  assertCondition(refreshedRecord, "La visita aggiornata non è stata trovata.");

  return refreshedRecord;
}
